(() => {
  if (window.wohnjoyCollectionReady) return;
  window.wohnjoyCollectionReady = true;
  const cardGalleries = new WeakMap();
  const cardImageLoads = new WeakMap();
  const normalizeColor = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/ß/g, 'ss').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const imageKey = value => {
    try { return new URL(value, location.origin).pathname; } catch { return ''; }
  };
  const getCardGallery = picker => {
    if (!picker) return null;
    if (cardGalleries.has(picker)) return cardGalleries.get(picker);
    const source = picker.querySelector('[data-wj-card-gallery]');
    if (!source) return null;
    try {
      const data = JSON.parse(source.textContent);
      const gallery = {
        colors: [...new Set((data.colors || []).map(normalizeColor))],
        primaryIds: new Set((data.primaryIds || []).map(String)),
        images: (data.images || []).map(image => ({ ...image, id: String(image.id), variantIds: (image.variantIds || []).map(String), colors: (image.colors || []).map(normalizeColor) }))
      };
      cardGalleries.set(picker, gallery);
      return gallery;
    } catch { return null; }
  };
  const imageColors = (image, gallery) => {
    if (image.colors.length) return image.colors;
    const alt = ' ' + normalizeColor(image.alt) + ' ';
    const matches = gallery.colors.filter(color => color && alt.includes(' ' + color + ' '));
    return matches.filter(color => !matches.some(other => other !== color && (' ' + other + ' ').includes(' ' + color + ' ')));
  };
  const findHoverImage = (gallery, primary, variantId, label) => {
    if (!gallery || !primary) return null;
    const color = normalizeColor(label);
    const candidates = gallery.images.filter(image => image.id !== primary.id);
    const associated = candidates.find(image => variantId && image.variantIds.includes(String(variantId)));
    if (associated) return associated;
    const isAnchor = image => gallery.primaryIds.has(image.id) || image.variantIds.length > 0;
    const tagged = candidates.find(image => {
      const colors = imageColors(image, gallery);
      return !isAnchor(image) && colors.length === 1 && colors[0] === color;
    });
    if (tagged) return tagged;
    const start = gallery.images.findIndex(image => image.id === primary.id);
    for (let index = start + 1; start >= 0 && index < gallery.images.length; index++) {
      const image = gallery.images[index];
      const colors = imageColors(image, gallery);
      if (isAnchor(image) || (colors.length === 1 && colors[0] !== color)) break;
      return image;
    }
    return null;
  };
  const setCardImage = (image, url, alt) => {
    image.closest('picture')?.querySelectorAll('source').forEach(source => source.remove());
    let source;
    try { source = new URL(url, location.origin); } catch {};
    const widths = [180, 240, 360, 480, 600, 720, 960, 1200];
    image.loading = 'lazy';
    image.decoding = 'async';
    image.fetchPriority = image.matches('.secondary-image,.miniture-mega-product__image--secondary') ? 'low' : 'high';
    if (source?.searchParams.has('width')) {
      const displayWidth = image.getBoundingClientRect().width || image.parentElement?.getBoundingClientRect().width || 300;
      const target = Math.ceil(displayWidth * Math.max(1, window.devicePixelRatio || 1));
      const fallbackWidth = widths.find(width => width >= target) || widths[widths.length - 1];
      const resized = width => {
        const candidate = new URL(source.href);
        candidate.searchParams.set('width', String(width));
        return candidate.href;
      };
      image.sizes = `auto, ${Math.ceil(displayWidth)}px`;
      image.srcset = widths.map(width => `${resized(width)} ${width}w`).join(', ');
      image.src = resized(fallbackWidth);
    } else {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
    }
    image.alt = alt;
  };
  const revealCardPrimary = (image, media) => {
    for (let layer = image; layer && layer !== media; layer = layer.parentElement) {
      layer.hidden = false;
      layer.classList.remove('hidden', 'invisible');
      layer.getAnimations?.().forEach(animation => animation.cancel());
      layer.style.setProperty('opacity', '1', 'important');
      layer.style.setProperty('visibility', 'visible', 'important');
      if (layer.matches('motion-element,picture')) {
        layer.style.setProperty('transform', 'none', 'important');
        if (layer.matches('motion-element')) layer.setAttribute('hold', '');
      }
    }
  };
  const updateCardImages = (card, selection) => {
    const media = card.querySelector('a.product__media,a.product-item__media--ratio,a.miniture-mega-product__media-link');
    const primaryImage = media?.querySelector('img.first-image,img.miniture-mega-product__image:not(.miniture-mega-product__image--secondary)') || media?.querySelector('img:not(.secondary-image)');
    if (!media || !primaryImage || !selection.image) return;
    const picker = card.querySelector('.wj-card-variants') || card;
    const gallery = getCardGallery(picker);
    const primary = gallery?.images.find(image => image.id === String(selection.imageId) || imageKey(image.url) === imageKey(selection.image));
    const title = card.querySelector('.product-item__name,.miniture-mega-product__title')?.textContent.trim() || '';
    const alt = title + (selection.label ? ' – ' + selection.label : '');
    cardImageLoads.get(media)?.abort();
    media.dataset.wjHoverReady = 'false';
    revealCardPrimary(primaryImage, media);
    setCardImage(primaryImage, primary?.url || selection.image, alt);
    let secondary = media.querySelector('img.secondary-image,img.miniture-mega-product__image--secondary');
    if (secondary) secondary.setAttribute('data-wj-hover-image', '');
    const hover = (secondary || picker?.dataset.wjHoverEnabled === 'true') ? findHoverImage(gallery, primary, selection.variantId, selection.label) : null;
    media.classList.toggle('show_secondary', Boolean(hover));
    if (!hover) {
      if (secondary) secondary.hidden = true;
      return;
    }
    if (!secondary) {
      secondary = document.createElement('img');
      secondary.className = media.matches('.miniture-mega-product__media-link') ? 'miniture-mega-product__image miniture-mega-product__image--secondary' : 'secondary-image opacity-0 product-image';
      secondary.width = primaryImage.width || 900;
      secondary.height = primaryImage.height || 900;
      secondary.decoding = 'async';
      secondary.setAttribute('data-wj-hover-image', '');
      media.append(secondary);
    }
    const loading = new AbortController();
    cardImageLoads.set(media, loading);
    secondary.hidden = false;
    const ready = () => {
      if (!loading.signal.aborted && cardImageLoads.get(media) === loading) media.dataset.wjHoverReady = 'true';
    };
    secondary.addEventListener('load', ready, { once: true, signal: loading.signal });
    secondary.addEventListener('error', () => {
      if (!loading.signal.aborted && cardImageLoads.get(media) === loading) {
        media.dataset.wjHoverReady = 'false';
        media.classList.remove('show_secondary');
      }
    }, { once: true, signal: loading.signal });
    setCardImage(secondary, hover.url, hover.alt || alt);
    if (secondary.complete && secondary.naturalWidth > 0) ready();
  };
  window.WohnjoyCardMedia = { select: updateCardImages };
  let returnFocus;
  const sidebar = () => document.querySelector('.sec__collection-main #CollectionSidebar');
  const openButton = () => document.querySelector('.wj-collection-toolbar btn-filter');
  const sortCollection = value => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    location.assign(url.href);
  };
  const closeSortDropdowns = target => {
    document.querySelectorAll('[data-wj-sort-dropdown][open]').forEach(dropdown => {
      if (!dropdown.contains(target)) dropdown.open = false;
    });
  };
  document.addEventListener('focusin', event => closeSortDropdowns(event.target));
  document.addEventListener('click', event => {
    closeSortDropdowns(event.target);
    const sortOption = event.target.closest('[data-wj-sort-option]');
    if (sortOption) {
      const control = sortOption.closest('[data-wj-sort-control]');
      control.querySelector('[data-wj-sort]').value = sortOption.dataset.wjSortOption;
      control.querySelector('[data-wj-sort-label]').textContent = sortOption.querySelector('span').textContent;
      control.querySelector('[data-wj-sort-dropdown]').open = false;
      sortCollection(sortOption.dataset.wjSortOption);
      return;
    }
    const shortcut = event.target.closest('[data-wj-filter]');
    if (shortcut) {
      const panel = sidebar();
      if (!panel) return;
      returnFocus = shortcut;
      if (!panel.classList.contains('open')) openButton()?.click();
      const group = [...panel.querySelectorAll('[data-wj-filter-group]')].find(node => node.dataset.wjFilterGroup === shortcut.dataset.wjFilter);
      if (group) {
        if (!group.classList.contains('active')) group.querySelector('.collapsible-heading')?.click();
        group.scrollIntoView({ block: 'nearest' });
        group.querySelector('.collapsible-heading')?.focus();
      }
      return;
    }
    if (event.target.closest('.wj-collection-toolbar btn-filter')) returnFocus = event.target.closest('btn-filter');
    const more = event.target.closest('[data-wj-variants-expand]');
    if (more) {
      event.preventDefault();
      const picker = more.closest('.wj-card-variants');
      if (!picker) return;
      const expanded = picker.classList.toggle('wj-card-variants--expanded');
      picker.querySelectorAll('[data-wj-variants-expand]').forEach(control => {
        control.setAttribute('aria-expanded', String(expanded));
        control.textContent = expanded ? '−' : control.dataset.wjMoreText;
        control.setAttribute('aria-label', expanded ? control.dataset.wjExpandedLabel : control.dataset.wjClosedLabel);
      });
      return;
    }
    const button = event.target.closest('[data-wj-variant]');
    if (!button || button.disabled) return;
    const card = button.closest('.product-item,.miniture-mega-product');
    if (!card) return;
    event.preventDefault();
    card.querySelectorAll('[data-wj-variant]').forEach(node => node.setAttribute('aria-pressed', String(node === button)));
    const colorName = card.querySelector('[data-wj-color-value]');
    if (colorName) colorName.textContent = button.dataset.label;
    for (const link of card.querySelectorAll('a[href]')) {
      const current = new URL(link.href, location.origin);
      const next = new URL(button.dataset.url, location.origin);
      if (current.origin === next.origin && current.pathname === next.pathname) link.href = next.href;
    }
    const bundlePicker = card.querySelector('variant-radios-bundle');
    if (bundlePicker) {
      const field = [...bundlePicker.querySelectorAll('[data-wj-option-position]')].find(node => node.dataset.wjOptionPosition === button.dataset.optionPosition);
      const select = field?.querySelector('.wj-bundle-option__select');
      if (select) {
        select.value = button.dataset.label;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    updateCardImages(card, button.dataset);
    if (button.dataset.variantId) {
      card.querySelectorAll('input[name="id"]').forEach(input => {
        input.value = button.dataset.variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      card.querySelectorAll('select-option[data-url],button-quickview[data-url]').forEach(control => {
        const url = new URL(control.dataset.url, location.origin);
        url.searchParams.set('variant', button.dataset.variantId);
        control.dataset.url = url.pathname + url.search;
      });
    }
    const price = card.querySelector('.product-item__price,.miniture-mega-product__price');
    const priceTemplate = button.querySelector('template[data-wj-price]');
    if (price && priceTemplate) price.replaceChildren(priceTemplate.content.cloneNode(true));
    const productId = button.dataset.productId;
    if (productId && window.WohnjoyWishlist?.readIds().includes(productId)) window.WohnjoyWishlist.remember(productId, button.dataset.variantId);
    if (card.matches?.('.wj-wishlist-card')) {
      card.dataset.price = button.dataset.price;
      card.dataset.variantId = button.dataset.variantId;
      card.dataset.available = 'true';
      const availability = card.querySelector('.wj-wishlist-card__availability');
      if (availability) availability.hidden = true;
      card.dispatchEvent(new CustomEvent('wohnjoy:wishlist-variant', { bubbles: true }));
    }
    const status = card.querySelector('[data-wj-variant-status]');
    if (status) status.textContent = button.getAttribute('aria-label');
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-wj-sort]')) return;
    sortCollection(event.target.value);
  });
  document.addEventListener('keydown', event => {
    const save = event.target.closest('.wj-card-save');
    if (save && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      save.click();
      return;
    }
    const dropdown = event.target.closest('[data-wj-sort-dropdown]');
    if (dropdown) {
      const options = [...dropdown.querySelectorAll('[data-wj-sort-option]')];
      const index = options.indexOf(event.target.closest('[data-wj-sort-option]'));
      if (event.key === 'Escape' && dropdown.open) {
        event.preventDefault();
        dropdown.open = false;
        dropdown.querySelector('summary').focus();
        return;
      }
      if (options.length && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        dropdown.open = true;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : event.key === 'ArrowDown' ? (index + 1) % options.length : (index <= 0 ? options.length - 1 : index - 1);
        options[next].focus();
        return;
      }
    }
    const panel = sidebar();
    const customButton = event.target.closest('.sec__collection-main btn-filter,.sec__collection-main close-filter,.sec__collection-main filter-item,.sec__collection-main facet-clear-all,.sec__collection-main facet-apply-canvas,.sec__collection-main .collapsible-heading');
    if (customButton && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      customButton.click();
      return;
    }
    if (!panel?.classList.contains('open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      panel.querySelector('close-filter')?.click();
    }
    if (event.key === 'Tab') {
      const controls = [...panel.querySelectorAll('a[href],button,input,select,[tabindex="0"]')].filter(node => !node.disabled && node.getClientRects().length);
      if (!controls.length) return;
      if (event.shiftKey && (document.activeElement === controls[0] || !panel.contains(document.activeElement))) { event.preventDefault(); controls.at(-1).focus(); }
      else if (!event.shiftKey && (document.activeElement === controls.at(-1) || !panel.contains(document.activeElement))) { event.preventDefault(); controls[0].focus(); }
    }
  });
  const variantParents = new WeakMap();
  const mobileCards = window.matchMedia('(max-width:749px)');
  const positionVariants = () => {
    document.querySelectorAll('.sec__collection-main #product-grid .wj-card-variants,.wj-card-surface .wj-card-variants').forEach(variants => {
      const card = variants.closest('.product-item');
      const imageBox = card?.querySelector('.product-item__inner');
      const info = card?.querySelector('.product-item__information');
      if (!imageBox || !info) return;
      if (!variantParents.has(variants)) variantParents.set(variants, imageBox);
      const horizontalMobile = mobileCards.matches && (card.closest('.sec__collection-main #product-grid') || card.matches('.wj-wishlist-card'));
      const parent = horizontalMobile ? info : variantParents.get(variants);
      const cta = horizontalMobile ? info.querySelector(':scope > .wj-card-price-row,:scope > .wj-card-controls,:scope > .wj-mobile-card-cta,:scope > .wj-wishlist-card__purchase') : null;
      if (variants.parentElement !== parent || (cta && variants.nextElementSibling !== cta)) {
        if (cta) parent.insertBefore(variants, cta);
        else parent.append(variants);
      }
    });
  };
  mobileCards.addEventListener('change', positionVariants);
  let previousOpen = false;
  const enhance = () => {
    document.querySelectorAll('[data-wj-sort-control]').forEach(control => {
      if (control.hasAttribute('data-wj-sort-ready')) return;
      if (!control.querySelector('[data-wj-sort]') || !control.querySelector('[data-wj-sort-dropdown]')) return;
      control.setAttribute('data-wj-sort-ready', '');
    });
    positionVariants();
    const panel = sidebar();
    if (!panel) return;
    panel.querySelectorAll('close-filter,filter-item,facet-clear-all,facet-apply-canvas,.collapsible-heading').forEach(node => {
      if (node.closest('.disabled')) return;
      node.setAttribute('tabindex', '0');
      node.setAttribute('role', 'button');
      if (node.matches('.collapsible-heading')) node.setAttribute('aria-expanded', String(node.parentElement.classList.contains('active')));
    });
    const isOpen = panel.classList.contains('open');
    openButton()?.setAttribute('aria-expanded', String(isOpen));
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', panel.querySelector('.sidebar-header h3')?.textContent.trim() || 'Filter');
    panel.setAttribute('aria-modal', 'true');
    panel.inert = !isOpen;
    if (isOpen && !previousOpen) panel.querySelector('close-filter')?.focus();
    if (!isOpen && previousOpen) {
      document.documentElement.style.removeProperty('padding-right');
      const target = returnFocus?.isConnected ? returnFocus : openButton();
      target?.focus({ preventScroll: true });
    }
    previousOpen = isOpen;
  };
  let queued = false;
  const enhancementTargets = '.wj-card-surface,.wj-card-variants,[data-wj-sort-control],#CollectionSidebar';
  new MutationObserver(records => {
    const relevant = records.some(record => {
      if (record.type === 'attributes') return Boolean(record.target.closest?.('#CollectionSidebar'));
      return [...record.addedNodes, ...record.removedNodes].some(node =>
        node.nodeType === 1 && (node.matches(enhancementTargets) || node.querySelector(enhancementTargets))
      );
    });
    if (!relevant || queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; enhance(); });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  enhance();
})();
